import React, { useEffect, useState, useMemo } from "react";
import { toast } from "react-hot-toast";
import axiosWrapper from "../../utils/AxiosWrapper";
import Heading from "../../components/Heading";
import { useSelector } from "react-redux";

/** HTML select values are strings; API + DB use numbers — keep in sync. */
function normalizeSemester(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n < 1 || n > 8) return 1;
  return n;
}

/** Populated refs can be null if subject/exam was deleted — avoids runtime crash. */
function markRowIsRenderable(mark) {
  return Boolean(
    mark?.subjectId &&
      mark?.examId &&
      typeof mark.subjectId === "object" &&
      typeof mark.examId === "object"
  );
}

function MarkResultCard({ mark, style }) {
  const subjectName = mark.subjectId?.name ?? "Subject";
  const examName = mark.examId?.name ?? "Exam";
  const total = mark.examId?.totalMarks ?? "—";

  return (
    <div
      style={style}
      className="border border-slate-200 rounded-xl p-4 bg-gradient-to-br from-white to-slate-50/80
        shadow-sm hover:shadow-md hover:border-blue-200/80 transition-all duration-300 ease-out
        animate-fade-in-up opacity-0"
    >
      <div className="flex justify-between items-center gap-3">
        <div className="min-w-0">
          <p className="font-medium text-gray-800 truncate">{subjectName}</p>
          <p className="text-sm text-gray-500 truncate">{examName}</p>
        </div>
        <div className="text-right shrink-0">
          <p className="text-lg font-semibold text-blue-600 tabular-nums">
            {mark.marksObtained ?? "—"}
          </p>
          <p className="text-sm text-gray-500">
            out of {total}
          </p>
        </div>
      </div>
    </div>
  );
}

const ViewMarks = () => {
  const userData = useSelector((state) => state.userData);
  const [dataLoading, setDataLoading] = useState(false);
  const [selectedSemester, setSelectedSemester] = useState(() =>
    normalizeSemester(userData?.semester ?? 1)
  );
  const [marks, setMarks] = useState([]);
  const userToken = localStorage.getItem("userToken");

  const fetchMarks = async (semester) => {
    const sem = normalizeSemester(semester);
    setDataLoading(true);
    toast.loading("Loading marks...");
    try {
      const response = await axiosWrapper.get(
        `/marks/student?semester=${sem}`,
        {
          headers: { Authorization: `Bearer ${userToken}` },
        }
      );

      if (response.data.success) {
        const rows = Array.isArray(response.data.data) ? response.data.data : [];
        setMarks(rows);
        const broken = rows.filter((m) => !markRowIsRenderable(m)).length;
        if (broken > 0) {
          toast(
            `${broken} mark row(s) skipped — linked subject or exam no longer exists.`,
            { icon: "⚠️", duration: 4500 }
          );
        }
      } else {
        toast.error(response.data.message);
        setMarks([]);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Error fetching marks");
      setMarks([]);
    } finally {
      setDataLoading(false);
      toast.dismiss();
    }
  };

  useEffect(() => {
    const sem = normalizeSemester(userData?.semester ?? 1);
    setSelectedSemester(sem);
    fetchMarks(sem);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData?.semester, userToken]);

  const handleSemesterChange = (e) => {
    const sem = normalizeSemester(e.target.value);
    setSelectedSemester(sem);
    fetchMarks(sem);
  };

  const { midTermMarks, endTermMarks } = useMemo(() => {
    const valid = marks.filter(markRowIsRenderable);
    return {
      midTermMarks: valid.filter((m) => m.examId?.examType === "mid"),
      endTermMarks: valid.filter((m) => m.examId?.examType === "end"),
    };
  }, [marks]);

  return (
    <div className="w-full max-w-5xl mx-auto mt-6 sm:mt-10 flex flex-col mb-10 px-2 sm:px-0">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 w-full mb-6">
        <Heading title="View Marks" />
        <div className="flex items-center gap-3">
          <label
            htmlFor="marks-semester"
            className="text-sm font-medium text-gray-700 whitespace-nowrap"
          >
            Semester
          </label>
          <select
            id="marks-semester"
            value={String(selectedSemester)}
            onChange={handleSemesterChange}
            disabled={dataLoading}
            className="px-4 py-2.5 border border-slate-300 rounded-lg bg-white text-gray-900
              focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500
              shadow-sm transition-shadow duration-200 disabled:opacity-60"
          >
            {[1, 2, 3, 4, 5, 6, 7, 8].map((sem) => (
              <option key={sem} value={sem}>
                Semester {sem}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div
        key={selectedSemester}
        className="grid grid-cols-1 md:grid-cols-2 gap-6 w-full animate-fade-in-up"
      >
        <section className="bg-white rounded-xl shadow-md p-5 sm:p-6 border border-slate-100/80">
          <h2 className="text-xl font-semibold mb-1 text-slate-800">
            Mid term marks
          </h2>
          <p className="text-xs text-slate-500 mb-4">Semester {selectedSemester}</p>
          {dataLoading ? (
            <div className="space-y-3 py-2">
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="h-16 rounded-lg bg-slate-100 animate-pulse"
                  style={{ animationDelay: `${i * 120}ms` }}
                />
              ))}
            </div>
          ) : midTermMarks.length > 0 ? (
            <div className="space-y-3">
              {midTermMarks.map((mark, idx) => (
                <MarkResultCard
                  key={mark._id}
                  mark={mark}
                  style={{ animationDelay: `${idx * 70}ms` }}
                />
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-sm py-6">
              No mid term marks for this semester.
            </p>
          )}
        </section>

        <section className="bg-white rounded-xl shadow-md p-5 sm:p-6 border border-slate-100/80">
          <h2 className="text-xl font-semibold mb-1 text-slate-800">
            End term marks
          </h2>
          <p className="text-xs text-slate-500 mb-4">Semester {selectedSemester}</p>
          {dataLoading ? (
            <div className="space-y-3 py-2">
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="h-16 rounded-lg bg-slate-100 animate-pulse"
                  style={{ animationDelay: `${i * 120}ms` }}
                />
              ))}
            </div>
          ) : endTermMarks.length > 0 ? (
            <div className="space-y-3">
              {endTermMarks.map((mark, idx) => (
                <MarkResultCard
                  key={mark._id}
                  mark={mark}
                  style={{ animationDelay: `${idx * 70}ms` }}
                />
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-sm py-6">
              No end term marks for this semester.
            </p>
          )}
        </section>
      </div>
    </div>
  );
};

export default ViewMarks;
